import React, { createContext, useEffect, useMemo, useState } from 'react'
import { useFeatureFlags } from '@node-space/hooks'

const DARK_MODE_STORAGE_KEY = 'darkMode'
const STORAGE_EVENT_NAME = 'storage'

export interface ThemeContext {
  darkMode: boolean
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>
}

export const ThemeContext = createContext<ThemeContext>({
  darkMode: false,
  setDarkMode: () => false,
})

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { enableDarkMode } = useFeatureFlags()

  const [darkMode, setDarkMode] = useState(() => {
    if (!enableDarkMode) {
      return false
    }

    const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY)
    if (stored !== null) {
      return JSON.parse(stored)
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    if (!enableDarkMode) {
      setDarkMode(false)
      return
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event?.key === DARK_MODE_STORAGE_KEY) {
        const newData = event?.newValue ? JSON.parse(event.newValue) : null
        setDarkMode(newData)
      }
    }

    window.addEventListener(STORAGE_EVENT_NAME, handleStorageChange)
    return () => {
      window.removeEventListener(STORAGE_EVENT_NAME, handleStorageChange)
    }
  }, [enableDarkMode])

  useEffect(() => {
    // Apply dark mode data attribute to document
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.removeAttribute('data-theme')
    }

    // Store preference
    localStorage.setItem('darkMode', JSON.stringify(darkMode))
  }, [darkMode])

  const value = useMemo(() => {
    return {
      darkMode: darkMode,
      setDarkMode: setDarkMode,
    }
  }, [darkMode, setDarkMode])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
